import React, {
    ChangeEvent,
    Ref,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react';
import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { flexCenterVertical } from '../../styles/mixins';
import HBInput from '../HBInput/HBInput';
import HBText from '../HBText/HBText';
import { Input } from '../HBInput/HBInput.styles';
import { HBInputProps } from '../HBInput/HBInput.types';
import { INPUT_SIZE_PROPS } from '../HBTextField/variables';
import { hoverTransition } from '../../styles/animations';
import { textFieldBaseStyles } from '../HBTextField/HBTextField.styles';
import {
    falseNotZero,
    HBMoneySizes,
    ALLOWED_SPECIAL_KEYS_ON_INPUT,
    HBMONEY_INPUT_PADDING,
    isNonNumberKey,
    getFloatRegex,
    isSecondDot,
    isSecondMinus,
    acceptsMinusAtCaret
} from '../HBMoneyInput/HBMoneyInput.utils';
import { getCurrencySymbol, formatCurrency } from '@honeybook/i18n';
import { Currency } from '../HBCurrency/HBCurrency';
import { MustIncludeOneOfTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { getInputTypeByDevice } from '../../utils/input.utils';
import { useLocale } from '@honeybook/i18n-react';

export interface HBMoneyInputProps extends Omit<HBInputProps, 'ref'> {
    /** CSS class applied to the root element. */
    className?: string;
    /** Input size; controls font size and padding. Defaults to `'md'`. */
    size?: HBMoneySizes;
    /** Test ID applied to the underlying input element for automated testing. */
    testId?: string;
    /** ISO 4217 currency code used to determine the displayed currency symbol. Defaults to `'USD'`. */
    currency?: Currency;
    /** Called on every change event in edit mode. Fires with the raw `ChangeEvent`; value is a numeric string. */
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    /** Called after the component enters edit mode and receives focus, useful for tracking field entry. */
    onPostFocus?: (e: ChangeEvent<HTMLInputElement>) => void;
    /** Adds a background highlight on hover in display mode to indicate the field is interactive. */
    isHoverable?: boolean;
    /** Disables editing; shows a not-allowed cursor and suppresses hover and focus styling. */
    isDisabled?: boolean;
    /** Ref forwarded to the underlying `<input>` element in edit mode. */
    inputRef?: Ref<HTMLInputElement>;
    /** Minimum fractional digits in display mode. Set to `2` to force trailing zeros (e.g. `$5.00` instead of `$5`). Omit to let the locale's default apply. */
    minFractionDigits?: number;
    /** HTML name attribute; used as the form data key and as a fallback for `data-testid` when `id` and `testIdName` are omitted. */
    name?: string;
    /** Allows a leading minus so the amount can be negative. Off by default — money fields reject negatives unless the surface has a reason to accept one, such as a line item that reduces a total. */
    allowNegative?: boolean;
}

const HBMoneyInput = ({
    className,
    size = 'md',
    value,
    onBlur,
    id,
    name,
    testIdName,
    testId,
    isHoverable,
    onChange,
    placeholder,
    inputMode = 'decimal',
    isDisabled,
    currency = 'USD',
    onPostFocus,
    type = 'number',
    inputRef: externalRef,
    minFractionDigits,
    allowNegative = false,
    ...rest
}: HBMoneyInputProps & MustIncludeOneOfTestIdProps) => {
    const floatRegex = getFloatRegex(allowNegative);
    const baseProps = {
        id,
        name,
        className,
        size,
        value,
        onBlur,
        placeholder,
        ...rest
    };

    const [isEditing, setIsEditing] = useState(false);
    const [symbolWidth, setSymbolWidth] = useState<number | null>(null);
    const _inputRef = useRef<HTMLInputElement>(null);
    const inputRef = externalRef || _inputRef;
    const symbolRef = useRef<HTMLSpanElement>(null);

    const handleActivate = useCallback(
        e => {
            if (!isDisabled) {
                setIsEditing(true);
                onPostFocus?.(e);
            }
        },
        [isDisabled, onPostFocus]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleActivate(e);
            }
        },
        [isDisabled, handleActivate]
    );

    const handleBlur = ev => {
        setIsEditing(false);
        if (onBlur) {
            onBlur(ev);
        }
    };

    useEffect(() => {
        if (isEditing && 'current' in inputRef) {
            inputRef.current.focus();
        }
    }, [inputRef, isEditing]);

    const { locale } = useLocale();
    const isFalseyNotZero = falseNotZero(value);
    const currencySymbol = getCurrencySymbol(currency);
    const formattedAmount = isFalseyNotZero
        ? ''
        : formatCurrency(Number(value), {
              currency,
              locale,
              maxFractionDigits: 3,
              minFractionDigits
          }).replace(currencySymbol, '');
    // The idle display is a role="button", whose aria-label would replace its
    // content, so the amount is added to the name explicitly.
    const displayAriaLabel =
        rest['aria-label'] && !rest['aria-labelledby']
            ? `${rest['aria-label']} ${
                  isFalseyNotZero
                      ? placeholder ?? ''
                      : `${currencySymbol}${formattedAmount}`
              }`.trim()
            : undefined;

    // Alphabetic-ending symbols (kr, SEK, DKK, NOK) sit flush against the number
    // like text — append \u00A0 so the measured width includes a word-space gap.
    // R$ ends with '$', so no extra gap is needed there.
    const editSymbolContent = /[a-zA-Z]$/.test(currencySymbol)
        ? currencySymbol + ' '
        : currencySymbol;

    // Measure the currency symbol width to set accurate input padding.
    // isFalseyNotZero is a dep: EditCurrencySymbol only mounts when !isFalseyNotZero, so we need to re-measure on empty→non-empty transition.
    useLayoutEffect(() => {
        if (isEditing && symbolRef.current) {
            const width = symbolRef.current.getBoundingClientRect().width;
            setSymbolWidth(width);
        }
    }, [isEditing, currency, isFalseyNotZero]);

    // if supplied with placeholder, show it. if not, display empty string if value is null, or 0 if empty string.
    const inputPlaceholder = placeholder || (isFalseyNotZero ? '' : '0');

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id || name,
        suffix: 'money-input',
        componentName: 'HBMoneyInput'
    });

    // since this bug in safari:
    // https://stackoverflow.com/questions/76645238/firefox-or-safari-javascript-parses-an-input-field-type-number-incorrectly
    // then to bypass it in mobile app we allow padding "tel" input. JIRA: HON-19808
    const deviceType =
        inputMode === 'decimal' && type !== 'tel'
            ? getInputTypeByDevice()
            : type;
    // a `number` input hides an intermediate '-' and exposes no caret
    const inputType = allowNegative ? 'tel' : deviceType;

    return (
        <Container
            className={className}
            size={size}
            isEditing={isEditing}
            isDisabled={isDisabled}
            $symbolWidth={isFalseyNotZero ? null : symbolWidth}
        >
            {isEditing ? (
                <>
                    {!isFalseyNotZero && (
                        <EditCurrencySymbol
                            ref={symbolRef}
                            size={size}
                            type="body18"
                            color="text.tertiary"
                        >
                            {editSymbolContent}
                        </EditCurrencySymbol>
                    )}
                    <HBInput
                        {...baseProps}
                        ref={inputRef}
                        testId={finalTestId}
                        selectOnFocus
                        blurOnEnterKey
                        onBlur={handleBlur}
                        min={allowNegative ? undefined : '0'}
                        onPaste={e => {
                            // get pasted text
                            const clipboardData =
                                // @ts-ignore
                                e.clipboardData || window.clipboardData;
                            let pastedData = clipboardData.getData('Text');

                            // a pasted sign survives only where it would land first
                            const isNegative =
                                allowNegative &&
                                pastedData.trim().startsWith('-') &&
                                acceptsMinusAtCaret(e.currentTarget);

                            // if pasted text contains illegal data remove it
                            if (
                                !floatRegex.test(pastedData) ||
                                (!isNegative && pastedData.includes('-'))
                            ) {
                                // \D drops the sign too, so re-apply it after
                                pastedData = pastedData.replace(/\D/g, '');
                                if (isNegative && pastedData) {
                                    pastedData = `-${pastedData}`;
                                }
                            }

                            document.execCommand(
                                'insertText',
                                false,
                                pastedData
                            );

                            e.preventDefault();
                        }}
                        onChange={e => {
                            let amount = e.currentTarget.value;
                            // @ts-ignore
                            const addedData = e.nativeEvent.data;

                            if (addedData && !floatRegex.test(addedData)) {
                                amount = value.toString();
                            }

                            e.currentTarget.value = amount;
                            onChange(e);
                        }}
                        onKeyDown={(
                            e: React.KeyboardEvent<HTMLInputElement>
                        ) => {
                            const ctrlOn = e.ctrlKey || e.metaKey;

                            // Allow special keys like backspace, delete, etc.
                            if (ALLOWED_SPECIAL_KEYS_ON_INPUT.includes(e.key)) {
                                return;
                            }
                            // allow only numbers, copy paste, a single dot and at most one leading minus
                            if (
                                isNonNumberKey(e, ctrlOn, allowNegative) ||
                                isSecondDot(e) ||
                                isSecondMinus(e)
                            ) {
                                e.preventDefault();
                            }
                        }}
                        type={inputType}
                        inputMode={inputMode}
                        step={0.01}
                        placeholder={inputPlaceholder}
                        onWheel={handleBlur}
                    />
                </>
            ) : (
                <CurrencyContainer
                    inputProps={baseProps}
                    $isHoverable={isHoverable}
                    data-testid={
                        finalTestId ? `${finalTestId}-currency` : undefined
                    }
                    role="button"
                    id={id}
                    aria-label={displayAriaLabel}
                    aria-labelledby={
                        id && rest['aria-labelledby']
                            ? `${rest['aria-labelledby']} ${id}`
                            : rest['aria-labelledby']
                    }
                    aria-describedby={rest['aria-describedby']}
                    tabIndex={isDisabled ? -1 : 0}
                    onFocus={handleActivate}
                    onClick={handleActivate}
                    onKeyDown={handleKeyDown}
                    $isDisabled={isDisabled}
                    aria-disabled={isDisabled || undefined}
                    title={String(value)}
                >
                    {isFalseyNotZero ? (
                        <Placeholder>{placeholder}</Placeholder>
                    ) : (
                        <>
                            <CurrencySymbol
                                size={size}
                                type="body18"
                                color={
                                    isDisabled
                                        ? 'text.disabled'
                                        : 'text.tertiary'
                                }
                            >
                                {currencySymbol}
                            </CurrencySymbol>
                            {formattedAmount}
                        </>
                    )}
                </CurrencyContainer>
            )}
        </Container>
    );
};

export default HBMoneyInput;

// -------------------------------- Styles -------------------------------- //

const Container = styled.div<{
    size: HBMoneySizes;
    isEditing: boolean;
    isDisabled: boolean;
    $symbolWidth: number | null;
}>`
    position: relative;
    cursor: ${props => (props.isEditing ? 'auto' : 'pointer')};
    ${props => (props.isDisabled ? 'cursor: not-allowed' : '')};
    ${Input} {
        padding-left: ${props =>
            props.$symbolWidth !== null
                ? `calc(${INPUT_SIZE_PROPS.system[props.size].paddingLeft} + ${
                      props.$symbolWidth + 2
                  }px)`
                : HBMONEY_INPUT_PADDING[props.size].paddingLeft};
        ::placeholder {
            font-size: ${props => INPUT_SIZE_PROPS.system[props.size].fontSize};
        }
    }
`;

const CurrencySymbol = styled(HBText)<{ size: HBMoneySizes }>`
    font-size: ${props => INPUT_SIZE_PROPS.system[props.size].fontSize};
    transform: translateX(-1px);
    margin-right: 2.5px;
`;

export const EditCurrencySymbol = styled(CurrencySymbol)`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: ${props => INPUT_SIZE_PROPS.system[props.size].paddingLeft};
`;

/* CurrencyContainer imitates the HBInput container props */
export const CurrencyContainer = styled.div<{
    inputProps: HBMoneyInputProps;
    $isHoverable: boolean;
    $isDisabled: boolean;
}>`
    ${props => textFieldBaseStyles(props.inputProps)};
    ${props => INPUT_SIZE_PROPS.system[props.inputProps.size]};
    ${props => props.inputProps.readOnly && 'pointer-events: none'};
    ${({ $isHoverable }) =>
        $isHoverable &&
        css`
            ${hoverTransition(
                'background',
                'unset',
                vars.colors.surface.itemInsetHover
            )};
        `}
    user-select: none;
    ${flexCenterVertical}
    ${({ $isDisabled }) =>
        $isDisabled &&
        css`
            border-color: ${vars.colors.stroke.disabled};
            color: ${vars.colors.text.disabled};
            cursor: not-allowed;
            &:focus,
            &:focus-within {
                background: ${vars.colors.surface.itemInset};
                border-color: ${vars.colors.stroke.disabled};
                outline: none;
            }
        `}
     ${props =>
        props.inputProps.error &&
        `background-color: ${vars.colors.surface.danger};
        border: none;
     `}
`;

const Placeholder = styled.div`
    color: ${vars.colors.text.tertiary};
`;
